import styled from '@emotion/styled';
import * as React from 'react';
import { useLayoutEffect, useMemo, useState } from 'react';
import { useFontsLoaded, useResizeObserver } from '../../hooks';
import { MenuItem, MenuItemWidget } from './MenuItemWidget';

export interface MenuWidgetProps {
  items: MenuItem[];
  className?: any;
}

enum CollapsePhase {
  // Step 1: Render all the items in order to determine their sizes
  // Step 2: Calculate the number of items that can be displayed based on the available width
  MEASURING = 'measuring',
  // Step 3: Render items based on available width
  COLLAPSING = 'collapsing'
}

// TODO resizing mechanism needs cleanup, move it to a hook or class
export const MenuWidget: React.FC<MenuWidgetProps> = (props) => {
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const [collapseFromIndex, setCollapseFromIndex] = useState(props.items.length);
  const [phase, setPhase] = useState<CollapsePhase>(CollapsePhase.MEASURING);
  const [sizeMap, setSizeMap] = useState<number[]>();

  const anchorSize = useResizeObserver(anchorRef);
  const menuSize = useResizeObserver(menuRef);
  const fontsLoaded = useFontsLoaded();

  // Reset to measuring phase when the number of items changes (This assumes that the items themselves don't change size)
  useLayoutEffect(() => {
    setPhase(CollapsePhase.MEASURING);
  }, [props.items.length]);

  // Reset the collapse index when the phase resets
  useLayoutEffect(() => {
    if (phase !== CollapsePhase.MEASURING) {
      return;
    }

    if (collapseFromIndex !== props.items.length) {
      setCollapseFromIndex(props.items.length);
    }
  }, [phase, collapseFromIndex]);

  // Figure out how much space each item needs to render
  useLayoutEffect(() => {
    if (phase !== CollapsePhase.MEASURING || !menuRef.current || !fontsLoaded) {
      return;
    }

    const map = [];
    for (let i = 0; i < props.items.length; i++) {
      const child = menuRef.current.children[i] as HTMLElement;
      const rect = child.getBoundingClientRect();
      map.push(rect.width);
    }

    // The "More" Button is supposed to always be present as the last child during the first render
    const moreButton = menuRef.current.lastElementChild as HTMLElement;
    const moreButtonRect = moreButton.getBoundingClientRect();
    map.push(moreButtonRect.width);

    setSizeMap(map);
    setPhase(CollapsePhase.COLLAPSING);
  }, [menuRef, phase, fontsLoaded]);

  useLayoutEffect(() => {
    if (phase !== CollapsePhase.COLLAPSING || !sizeMap || !anchorSize || !menuSize) {
      return;
    }

    const availableWidth = anchorSize.width;
    let maxUsedWith = 0;
    // Exclude the "More" button width from the initial calculation
    for (let i = 0; i < sizeMap.length - 1; i++) {
      maxUsedWith += sizeMap[i];
    }

    // All items fit, no need to collapse anything
    if (maxUsedWith <= availableWidth) {
      setCollapseFromIndex(props.items.length);
      return;
    }

    // Add back the "More" button width since we need to account for the space it will also use
    maxUsedWith += sizeMap[sizeMap.length - 1];

    for (let i = props.items.length - 1; i >= 0; i--) {
      if (maxUsedWith - sizeMap[i] < availableWidth) {
        // Excluding this item and all that follow it will make everything fit
        setCollapseFromIndex(i - 1);
        return;
      }

      maxUsedWith -= sizeMap[i];
    }

    if (maxUsedWith > availableWidth) {
      // Even the "More" button doesn't fit, collapse all items
      setCollapseFromIndex(-1);
      return;
    }

    setCollapseFromIndex(props.items.length);
  }, [phase, menuSize, anchorSize, sizeMap]);

  const moreItem = useMemo((): MenuItem => {
    return {
      to: '#',
      label: 'More',
      items:
        // More button will only show when there are sub items, so for the measurement phase we add all items to ensure the down chevron shows
        phase === CollapsePhase.MEASURING
          ? props.items
          : props.items.filter((i, idx) => {
              return idx > collapseFromIndex;
            })
    };
  }, [props.items, collapseFromIndex, phase]);

  return (
    <S.MenuAnchor ref={anchorRef}>
      {/*Hide the menu during the measurement phase to prevent the user from seeing janky items appearing and disappearing*/}
      <S.Menu className={props.className} ref={menuRef} shouldHide={phase === CollapsePhase.MEASURING}>
        {props.items.map((i: MenuItem, idx: number) => {
          if (idx > collapseFromIndex) {
            return null;
          }

          return <MenuItemWidget item={i} key={i.label} />;
        })}
        {(collapseFromIndex !== props.items.length || phase === CollapsePhase.MEASURING) && (
          // Show the More Items button only during the measurement phase or if there are actually items to collapse
          <MenuItemWidget item={moreItem} />
        )}
      </S.Menu>
    </S.MenuAnchor>
  );
};

namespace S {
  export const MenuAnchor = styled.div`
    position: relative;
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 0;

    // Ensures that this element can shrink smaller than its min required content width
    min-width: 0;
    height: 100%;
  `;

  export const Menu = styled.div<{ shouldHide?: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    opacity: ${(p) => (p.shouldHide ? 0 : null)};

    display: flex;
    align-items: center;
  `;
}
